import { useKeycloak } from "@react-keycloak/web";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { environment } from "../environment";
import {
  barchartDataMock,
  barchartOptions,
  dougnutChartMock,
  linechartMock,
  linechartMock2,
  linechartOptionsMock,
  piechartMock,
} from "../mock/chartJS.mock";
import { calculateAllTransactionsCardData, getGraphData, sortTransactionDataByDate } from "../utils/chart.utils";
import { CurrencyUtil } from "../utils/currency.util";
import { DateUtil } from "../utils/date.util";
import { getCurrentEndDate, getVendingStartDate } from "../utils/filter.utils";
import { useAxios } from "./useAxios";
import { useUserContext } from "../context/user.context";
import { toast } from "react-toastify";

export const useVendingData = () => {
  const format = "dd.MM.yyyy HH:mm:ss";

  //FILTERS FROM ENV
  const [startDateVending, setStartDateVending] = useState(getVendingStartDate());
  const [endDateVending, setEndDateVending] = useState(getCurrentEndDate());

  const { t } = useTranslation();
  const [summarizedVendingData, setSummarizedVendingData] = useState([]);

  const [paymentChannels, setPaymentChannels] = useState(["Select Payment Type"]);
  const [monthsPeriod, setMonthsPeriod] = useState(["Select Month(s)"]);
  const [vendingStatuses, setVendingStatuses] = useState(["Select status(es)"]);
  const [deviceNames, setDeviceNames] = useState(["Select machine(s)"]);

  //NEWBACKEND
  const [totalNumberOfTransactions, setTotalNumberOfTransactions] = useState(0);
  const [averageNumberOfTransactions, setAverageNumberOfTransactions] = useState(0);
  const [totalPrices, setTotalPrices] = useState(0);
  const [averagePrice, setAveragePrice] = useState(0);
  const [averageAmountPerTransactions, setAverageAmountPerTransactions] = useState(0);

  const [loadingFlag, setLoadingFlag] = useState(false);

  const [notReadyVending, setNotReadyVending] = useState(false);

  const { merchantId } = useUserContext();

  const { axiosInstance } = useAxios();

  const { initialized } = useKeycloak();

  const filterVendingData = (data, selectedDateRange, selectedPaymentChannel, selectedStatus, selectedMonthYear, selectedDaysPeriod, selectedMachineNames) => {
    /* console.log("FILTER ITEMS RECEIVED", {
      data,
      selectedDateRange,
      selectedPaymentChannel,
      selectedStatus,
      selectedMonthYear,
      selectedDaysPeriod,
    }); */
    //console.log("FILTER ITEMS RECEIVED ", data)
    return data?.filter((transaction) => {
      const channelsValues = (selectedPaymentChannel || []).map((pay) => pay.value); 

      const deviceNames = (selectedMachineNames || []).map((name) => name.value); 

      const monthValues = (selectedMonthYear || []).map((sd) => sd.value.month);
      const yearValues = (selectedMonthYear || []).map((sd) => sd.value.year);

      const statusValues = (selectedStatus || []).map((status) => status.value);
      const itemYear = new Date(DateUtil.convertBackendDateFormat(transaction.date)).getFullYear();
      const itemMonth = new Date(DateUtil.convertBackendDateFormat(transaction.date)).getMonth() + 1;

      //console.log("FILTER ITEMS", { channelsValues, selectedDaysPeriod, monthValues, yearValues, itemYear, itemMonth, transaction })

      //Filters
      //const channel = !selectedPaymentChannel.length || channelsValues.includes(transaction.payment_channel);
      const channel = !selectedPaymentChannel.length || channelsValues.includes(transaction.paymentChannel);
      const state = !selectedStatus.length || statusValues.includes(transaction.status);
      const month = !selectedMonthYear.length || (yearValues.includes(itemYear) && monthValues.includes(itemMonth));
      const days = !selectedDaysPeriod || new Date() - new Date(transaction.date) < parseInt(selectedDaysPeriod.value) * 24 * 60 * 60 * 1000
      const name = !selectedMachineNames?.length || deviceNames?.includes(transaction.deviceRef)
      //console.log("FILTER ITEMS END", { transaction, channel, month, days })

      return channel && month && days && state && name;
    });
  };

  //BARCHART AMOUNT, COUNT
  const [amountsByDayOfWeekByPaymentMethod, setAmountsByDayOfWeekByPaymentMethod] = useState({
    options: barchartOptions,
    data: barchartDataMock,
  });
  const [countByDayBarGraph, setCountByDayBarGraph] = useState({
    options: barchartOptions,
    data: barchartDataMock,
  });

  //LINE CHARTS AMOUNT BY DAY
  const [amountByChannelDailyLinechart, setAmountByChannelDailyLinechart] = useState({
    linechartData: linechartMock2,
    linechartOptions: linechartOptionsMock,
  });

  //LINE CHARTS COUNT BY DAY
  const [transactionsByDayGraphData, setTransactionsByDayGraph] = useState({
    linechartData: linechartMock,
    linechartOptions: linechartOptionsMock,
  });

  //LINE CHARTS AMOUNT BY DAY AND PAYMENT METHOD
  const [amountByDayLinechart, setAmountByDayLinechart] = useState({
    linechartData: linechartMock,
    linechartOptions: linechartOptionsMock,
  });

  //LINE CHARTS COUNT BY DAY AND PAYMENT METHOD
  const [transactionsByPaymentGraphData, setTransactionsByPaymentGraphData] = useState({
    linechartData: linechartMock2,
    linechartOptions: linechartOptionsMock,
  });

  //PIE CHART BY AMOUNT
  const [amountByOperatorTotal, setAmountByOperatorTotal] = useState({
    piedata: piechartMock,
  });

  //DOUGHNUT CHART COUNT AND PERCENTAGE
  const [amountAndPaymentMethod, setAmountAndPaymentMethod] = useState({
    piedata: dougnutChartMock,
  });

  const calculateVendingData = (data) => {
    const vendingData = sortTransactionDataByDate(data);

    const { averageAmountPerTrans, averageAmountPrices, averageNumberTransactions, totalAmountPrices, totalTransactions } =
      calculateAllTransactionsCardData(vendingData);

    //TRANSACTION CARDS
    setTotalNumberOfTransactions(totalTransactions);
    setAverageNumberOfTransactions(averageNumberTransactions);
    setTotalPrices(totalAmountPrices);
    setAveragePrice(averageAmountPrices);
    setAverageAmountPerTransactions(averageAmountPerTrans);

    //CALCULATION IN CHART UTILS
    const {
      amountsByDayOfWeekByPaymentMethodBarChartJsData,
      countsByDayOfWeekByPaymentMethodBarChartJsData,

      amountByDayLinechartData,
      countByDayLinechartData,
      amountByChannelsLinechartData,
      countByChannelsLinechartData,

      totalAmountsByOperatorPieChartJSData,
      totalAmountAndPercentageByPaymentChannelsPieChartJsData,
    } = getGraphData(data, paymentChannels, startDateVending, endDateVending, t);

    //PAYMENT OVERVIEW CALCULATIONS
    //LINECHART BY AMOUNT - DAILY
    setAmountByChannelDailyLinechart(amountByDayLinechartData);
    //LINECHART BY AMOUNT - PAYMENT METHOD
    setAmountByDayLinechart(amountByChannelsLinechartData);

    //BARCHART BY AMOUNT
    setAmountsByDayOfWeekByPaymentMethod(amountsByDayOfWeekByPaymentMethodBarChartJsData);

    //PERFORMANCE OVERVIEW CALCULATIONS
    //LINECHART BY COUNT - DAILY
    setTransactionsByDayGraph(countByDayLinechartData);
    //LINECHART BY COUNT AND PAYMENT METHOD
    setTransactionsByPaymentGraphData(countByChannelsLinechartData);

    //BARCHART BY COUNT
    setCountByDayBarGraph(countsByDayOfWeekByPaymentMethodBarChartJsData);

    //PIE
    setAmountByOperatorTotal(totalAmountsByOperatorPieChartJSData);

    //DOUGHNUT
    setAmountAndPaymentMethod(totalAmountAndPercentageByPaymentChannelsPieChartJsData);
  };

  const getSummarizedVendingDataEndpoint = (startDateVending, endDateVending) => {
    setLoadingFlag(true);

    const startDateConverted = DateUtil.formatDate(new Date(startDateVending), "dd.MM.yyyy HH:mm:ss");
    const endDateConverted = DateUtil.formatDate(new Date(endDateVending), "dd.MM.yyyy HH:mm:ss");

    const url = `${process.env.REACT_APP_MANAGEMENT_BACKEND_URL}${environment.PGWSummaryBufferedEndpointNewEndpoint}/V2/${merchantId}/${startDateConverted}/${endDateConverted}`;

    axiosInstance?.current
      ?.get(url)
      .then((res) => {
        setLoadingFlag(false);
        const vendingData = res?.data.flatMap((obj) => {
          if (obj && obj.date) {
            obj.date = DateUtil.convertDateStringToBackendDateStringNoHours(obj.date);
          }
          return obj;
        });

        setSummarizedVendingData(vendingData);

        //UNIQUE PAYMENT CHANNELS
        const uniquePaymentChannels = [
          ...new Set(
            vendingData?.map((types) => {
              if (types.paymentChannel === "PFC") {
                types.paymentChannel = "PostFinance";
              } else if (types.paymentChannel === "ECA") {
                types.paymentChannel = "MasterCard";
              } else if (types.paymentChannel === "VIS") {
                types.paymentChannel = "Visa";
              } else if (types.paymentChannel === "MAE") {
                types.paymentChannel = "MaestroCard";
              }
              return types.paymentChannel;
            })
          )
        ];

        setPaymentChannels(uniquePaymentChannels);

        //UNIQUE STATUS TYPES
        const uniqueVendingStatuses = [...new Set(vendingData?.map((paymentTypes) => paymentTypes.status))];
        setVendingStatuses(uniqueVendingStatuses);

        //UNIQUE MONTHS
        const uniqueDatesArray = [...new Set(vendingData?.map((types) => new Date(types.date)).flat())];

        const uniqueMonths = DateUtil.getUniqueMonthsYears(uniqueDatesArray, "VENDING MONTHS PERIOD");
        setMonthsPeriod(uniqueMonths);

        //UNIQUE MACHINES
        const uniqueMachines = [...new Set(vendingData?.map((types) => types.deviceRef).flat())];
        setDeviceNames(uniqueMachines)
        //ALL CALCULATIONS FOR GRAPHS
        calculateVendingData(vendingData);

        //console.log("NEW BACKEND VENDING DATA => ", vendingData)
      })
      .catch((err) => {
        setLoadingFlag(false);
        if(err?.response?.status === 500){
          ShowToastMessage(t("toastr.vending.vendingData")+t("toastr.general.cantShow.error500"));
        }else if(err?.response?.status === 401){
          ShowToastMessage(t("toastr.vending.vendingData")+t("toastr.general.cantShow.error401"));
        }else if(err?.response?.status === 404){
          ShowToastMessage(t("toastr.vending.vendingData")+t("toastr.general.cantShow.error404"));
        }else if(err?.response?.status === 204){
          ShowToastMessage(t("toastr.vending.vendingData")+t("toastr.general.cantShow.error204"));
        }
        console.log("THERE ARE ERRORS FOR VENDING DATA => ", err);
      });
  };

  const callPeriodDataFetch = (startDateVending, endDateVending) => {
    //Set last Start/End date
    setStartDateVending(startDateVending);
    setEndDateVending(endDateVending);
    if (notReadyVending && merchantId && initialized) {
      getSummarizedVendingDataEndpoint(startDateVending, endDateVending);
    } //NEW BACKEND TEST
  };

  const ShowToastMessage = (text) =>
    toast(<p className="mx-2 tx-16 d-flex align-items-center mb-0 ">{text}</p>, {
      position: toast.POSITION.TOP_RIGHT,
      hideProgressBar: true,
    });

  return {
    //NEWBACKEND
    summarizedVendingData,
    graphData: {
      amountsByDayOfWeekByPaymentMethod,
      countByDayBarGraph,
      amountByChannelDailyLinechart,
      transactionsByDayGraphData,
      amountByDayLinechart,
      transactionsByPaymentGraphData,
      amountByOperatorTotal,
      amountAndPaymentMethod,
    },
    transactionCardsData: [
      {
        title: `${t("transactions.cards.totalTransactions")}`,
        subtitle: `${t("transactions.cards.totalTransactionsSub")}`,
        value: CurrencyUtil.formatAsNumber(totalNumberOfTransactions).slice(0, -3),
        type: "number",
      },
      {
        title: `${t("transactions.cards.averageTransactions")}`,
        subtitle: `${t("transactions.cards.averageTransactionsSub")}`,
        value: CurrencyUtil.formatAsNumber(averageNumberOfTransactions),
        type: "number",
      },
      {
        title: `${t("transactions.cards.totalAmount")}`,
        subtitle: `${t("transactions.cards.totalAmountSub")}`,
        value: CurrencyUtil.formatAsCurrency(totalPrices),
        type: "currency",
      },
      {
        title: `${t("transactions.cards.averagePrices")}`,
        subtitle: `${t("transactions.cards.averagePricesSub")}`,
        value: CurrencyUtil.formatAsCurrency(averagePrice),
        type: "currency",
      },
      {
        title: `${t("transactions.cards.singleAverage")}`,
        subtitle: `${t("transactions.cards.singleAverageSub")}`,
        value: CurrencyUtil.formatAsCurrency(averageAmountPerTransactions),
        type: "currency",
      },
    ],
    paymentChannels,
    vendingStatuses,
    monthsPeriod,
    deviceNames,
    callFilterVending: (startDateVending, endDateVending) => callPeriodDataFetch(startDateVending, endDateVending),
    calculateVendingData,
    filterVendingData,
    loadingFlag,
    setStartDateVending,
    setEndDateVending,
    notReadyVending,
    setNotReadyVending,
  };
};
